import { Module } from 'vuex'
import { videoDB, videoDbUtils, VideoHistory, VideoMessage } from '@/store/modules/video/database'

export interface VideoState {
    historyList: VideoHistory[]
}

const state: VideoState = {
    historyList: []
}

const getters = {
    historyList: (state: VideoState) => state.historyList,

    displayHistoryList: (state: VideoState) => state.historyList,

    lastHistoryId: (state: VideoState) => {
        return state.historyList[state.historyList.length - 1]?.id
    },

    lastHistory: (state: VideoState) => {
        return state.historyList[state.historyList.length - 1]
    },

    isGenerating: (state: VideoState) => {
        return state.historyList.some(h =>
            h.messageList.some(m => m.from === 'receive' && (m.status === 'thinking' || m.status === 'processing'))
        )
    }
}

const mutations = {
    SET_HISTORY_LIST(state: VideoState, historyList: VideoHistory[]) {
        state.historyList = historyList
    },

    ADD_HISTORY(state: VideoState, history: VideoHistory) {
        state.historyList.push(history)
    },

    ADD_MESSAGE(state: VideoState, { hId, message }: { hId: number; message: VideoMessage }) {
        const history = state.historyList.find(h => h.id === hId)
        if (history) {
            history.messageList.push(message)
        }
    },

    UPDATE_MESSAGE(state: VideoState, { hId, msgId, updates }: { hId: number; msgId: number; updates: Partial<VideoMessage> }) {
        const history = state.historyList.find(h => h.id === hId)
        if (!history) return
        const msgIndex = history.messageList.findIndex(m => m.id === msgId)
        if (msgIndex !== -1) {
            history.messageList.splice(msgIndex, 1, { ...history.messageList[msgIndex], ...updates })
        }
    }
}

const actions = {
    async initDatabase({ dispatch }: any) {
        try {
            await videoDB.open()
            await dispatch('loadHistoryList')
        } catch (error) {
            console.error('Video init database failed:', error)
        }
    },

    async loadHistoryList({ commit }: any) {
        try {
            const historyList = await videoDbUtils.getUserHistories()

            // 只保留最近 20 条，清理旧数据
            const MAX_HISTORY = 20
            if (historyList.length > MAX_HISTORY) {
                const toRemove = historyList.slice(0, historyList.length - MAX_HISTORY)
                for (const h of toRemove) {
                    if (h.id) videoDbUtils.deleteHistory(h.id)
                }
                commit('SET_HISTORY_LIST', historyList.slice(-MAX_HISTORY))
            } else {
                commit('SET_HISTORY_LIST', historyList)
            }
        } catch (error) {
            console.error('Video load history failed:', error)
        }
    },

    async createNewHistory({ commit }: any): Promise<number> {
        const { hId } = await videoDbUtils.createHistory()
        const history: VideoHistory = { id: hId, createTime: Date.now(), userId: '', messageList: [] }
        commit('ADD_HISTORY', history)
        return hId
    },

    async addMessage({ commit, state: s }: any, { hId, message }: { hId: number; message: Omit<VideoMessage, 'id'> }): Promise<number> {
        const history = s.historyList.find((h: VideoHistory) => h.id === hId)
        const id = history?.messageList.length || 0
        const fullMessage: VideoMessage = { ...message, id } as VideoMessage

        commit('ADD_MESSAGE', { hId, message: fullMessage })
        await videoDbUtils.addMessage(hId, message)

        return id
    },

    async updateMessage({ commit }: any, { hId, msgId, updates }: { hId: number; msgId: number; updates: Partial<VideoMessage> }) {
        commit('UPDATE_MESSAGE', { hId, msgId, updates })
        videoDbUtils.updateMessage(hId, msgId, updates)
    }
}

const videoModule: Module<VideoState, any> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}

export default videoModule
